import { Button, Tabs } from "antd";
import { useEffect, useState, type FC } from "react";
import { getRatesAndDosageTabs } from "./constant";
import { useTranslation } from "react-i18next";
import { formatTranslation } from "../../../shared/utils/translationUtils";
import ProtocolDrawer from "../../../shared/layout/ProtocolDrawer";
import { useNewTrialState } from "../hooks/NewTrialStateProvider";
import { asNonEmpty } from "../../../shared/utils/stringUtils";
import { InfoCircleOutlined } from "@ant-design/icons";
import { useTrialsRouter } from "../../../shared/custom-hooks/useTrialsRouter";

interface RatesDosageComponentProps {
  trialData: any;
}

const RatesDosageComponent: FC<RatesDosageComponentProps> = ({ trialData }) => {
  const {
    trialProtocolData,
    setIsFertilizerTabActive,
    activeRatesTab,
    setActiveRatesTab,
    setRatesDosage,
    trialInfo,
  } = useNewTrialState();

  const { t } = useTranslation();
  const [showDrawer, setShowDrawer] = useState(false);

  const trialUOM =
    trialInfo?.trial_plots?.[0]?.properties?.seeds?.measure_unit ??
    trialData?.trial_plots?.[0]?.properties?.seeds?.measure_unit;

  useEffect(() => {
    setRatesDosage((prevRatesDosage) => ({
      ...prevRatesDosage,
      unit_of_rate:
        asNonEmpty(trialUOM) ??
        asNonEmpty(prevRatesDosage.unit_of_rate) ??
        "seeds/ha",
    }));
  }, [trialUOM, setRatesDosage]);

  const { orgId, workspaceId } = useTrialsRouter();

  const extraHeaders = {
    "org-id": orgId ?? "",
    "workspace-id": workspaceId ?? "",
  };

  const hideDrawer = (): void => {
    setShowDrawer(false);
  };

  const openDrawer = (): void => {
    setShowDrawer(true);
  };

  const tabItems = getRatesAndDosageTabs(trialProtocolData);
  const firstTabKey = (tabItems?.[0]?.key as string) ?? "seed";
  const isActiveTabValid = tabItems?.some(
    (item) => item?.key === activeRatesTab
  );

  useEffect(() => {
    const firstProtocolType = trialProtocolData?.protocol_type?.[0] as string;

    if (!firstProtocolType) return;

    if (!trialProtocolData?.protocol_type?.includes(activeRatesTab)) {
      setIsFertilizerTabActive(firstProtocolType === "nutrient");
      setActiveRatesTab(firstProtocolType);
    }

    return () => {
      setIsFertilizerTabActive(false);
    };
  }, [
    trialProtocolData,
    activeRatesTab,
    setActiveRatesTab,
    setIsFertilizerTabActive,
  ]);

  return (
    <div className="rates-dosage-main">
      <div className="title-sec">
        <h3 className="main-title">
          {formatTranslation(t("newTrial.ratesDosage.title"))}
        </h3>
      </div>

      <ProtocolDrawer
        open={showDrawer}
        onClose={hideDrawer}
        protocolId={trialProtocolData?.id ?? ""}
        extraHeaders={extraHeaders}
      />

      <Tabs
        activeKey={isActiveTabValid ? activeRatesTab : firstTabKey}
        tabBarExtraContent={
          <Button
            type="text"
            onClick={openDrawer}
            icon={<InfoCircleOutlined />}
          >
            {formatTranslation(t("newTrial.ratesAndDosage.protocolInfo"))}
          </Button>
        }
        onChange={(key) => {
          setIsFertilizerTabActive(key === "nutrient");
          setActiveRatesTab(key);
        }}
        items={tabItems}
      />
    </div>
  );
};

export default RatesDosageComponent;
